import { map } from 'lodash'
import { useCallback, useMemo } from 'react'
import { graphql, useFragment, useMutation } from 'react-relay'

import {
  type useChildrenPreferenceCriteria_query$data,
  type useChildrenPreferenceCriteria_query$key
} from '@/__generated__/useChildrenPreferenceCriteria_query.graphql'
import { type useChildrenPreferenceCriteriaMutation } from '@/__generated__/useChildrenPreferenceCriteriaMutation.graphql'

type CommitAsyncFn = (childrenPreferenceIds: string[]) => Promise<useChildrenPreferenceCriteria_query$data>

export const useChildrenPreferenceCriteria = (
  queryRef?: useChildrenPreferenceCriteria_query$key | null | undefined
): [string[], CommitAsyncFn, boolean] => {
  const queryData = useFragment(
    graphql`
      fragment useChildrenPreferenceCriteria_query on Query {
        currentUser {
          childrenPreferenceCriteria(orderBy: [CHILDREN_PREFERENCE_BY_CHILDREN_PREFERENCE_ID__SEQUENCE_ASC]) {
            nodes {
              childrenPreferenceId
            }
          }
        }
      }
    `,
    queryRef
  )
  const data = useMemo(
    () => map(queryData?.currentUser?.childrenPreferenceCriteria.nodes, 'childrenPreferenceId'),
    [queryData?.currentUser?.childrenPreferenceCriteria.nodes]
  )
  const [commit, isPending] = useMutation<useChildrenPreferenceCriteriaMutation>(graphql`
    mutation useChildrenPreferenceCriteriaMutation($input: UpdateChildrenPreferenceCriteriaInput!) {
      updateChildrenPreferenceCriteria(input: $input) {
        query {
          ...useChildrenPreferenceCriteria_query
        }
      }
    }
  `)

  const commitAsync = useCallback<CommitAsyncFn>(
    (childrenPreferenceIds: string[]) =>
      new Promise((resolve, reject) =>
        commit({
          onCompleted: (response, errors) => {
            if (errors) {
              reject(errors)
            } else {
              // @ts-ignore false negative
              resolve(response)
            }
          },
          variables: {
            input: {
              childrenPreferenceIds
            }
          }
        })
      ),
    [commit]
  )

  return [data, commitAsync, isPending]
}
